import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ChartType, DashboardDataSource, Widget } from '@/api/types'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet'
import { Separator } from '@/shared/ui/separator'
import { Textarea } from '@/shared/ui/textarea'
import { useWidgetMutations } from '../mutations'
import { DataSourceForm } from './data-source-form'

/** What the backend accepts in either field. */
const MAX = 255

const CHART_TYPES = [
  'bar',
  'line',
  'pie',
  'area',
  'scatter',
] as const satisfies readonly ChartType[]

/**
 * Everything about one widget that is not a question about data.
 *
 * A panel rather than a dialog: configuring a widget is done while looking at
 * it, and a dialog over the middle of the board would cover the thing being
 * changed. Upstream reserves 352px of the page for the same panel and hides it
 * behind an edit mode; a sheet is the same width without a mode to be in.
 *
 * **The text commits when it loses focus**, which is the rule the grid's cell
 * editor settled on and for the same reason: it is the only path that also
 * covers clicking away to something else, and a request per keystroke would put
 * a rename of "Revenue" through six of them. The chart shape commits at once --
 * a select has no half-chosen state to wait out.
 */
export function WidgetSettings({
  dashboardId,
  widget,
  source,
  onOpenChange,
}: {
  dashboardId: number
  widget: Widget
  /**
   * Where this widget's numbers come from, if the list still holds it.
   *
   * Absent means trashed, and there is nothing to configure: the questions the
   * form asks are all about a data source, and offering them with none would be
   * offering to configure something that is not there.
   */
  source: DashboardDataSource | undefined
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { update } = useWidgetMutations(dashboardId)
  const [title, setTitle] = useState(widget.title)
  const [description, setDescription] = useState(widget.description)

  const named = title.trim() !== ''

  return (
    // Not modal, which is the whole difference between this and a dialog: the
    // board stays readable and clickable while a widget is being configured,
    // where a modal would hide it from assistive technology and take every
    // click. Dismissing on a click outside is off for the same reason -- the
    // panel closes when it is closed, not when somebody reaches past it.
    <Sheet open modal={false} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full gap-0 overflow-y-auto sm:max-w-sm"
        onInteractOutside={(event) => {
          event.preventDefault()
        }}
      >
        <SheetHeader>
          <SheetTitle>{t('widget.settings')}</SheetTitle>
          <SheetDescription>{t('widget.settingsDescription')}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-4 pb-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="widget-title">{t('widget.titleLabel')}</Label>
            <Input
              id="widget-title"
              value={title}
              maxLength={MAX}
              onChange={(event) => {
                setTitle(event.target.value)
              }}
              onBlur={() => {
                // Left as typed when it is not a title, rather than put back to
                // what it was: somebody who emptied the box is midway through
                // replacing it, and restoring the old name under their cursor
                // would take away what they were about to overwrite.
                if (!named || title.trim() === widget.title) return
                update(widget.id, { title: title.trim() })
              }}
            />
            {!named && (
              <p className="text-destructive text-sm">{t('widget.titleRequired')}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="widget-description">{t('widget.descriptionLabel')}</Label>
            <Textarea
              id="widget-description"
              rows={2}
              value={description}
              maxLength={MAX}
              onChange={(event) => {
                setDescription(event.target.value)
              }}
              onBlur={() => {
                if (description === widget.description) return
                update(widget.id, { description })
              }}
            />
          </div>

          {widget.type === 'lithos_chart' && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="widget-chart-type">{t('widget.chartType')}</Label>
              <NativeSelect
                id="widget-chart-type"
                className="w-full"
                value={widget.chart_type ?? 'bar'}
                onChange={(event) => {
                  update(widget.id, { chart_type: event.target.value as ChartType })
                }}
              >
                {CHART_TYPES.map((type) => (
                  <NativeSelectOption key={type} value={type}>
                    {t(`widget.${type}`)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          )}

          {source !== undefined && (
            <>
              <Separator className="my-1" />
              <p className="text-muted-foreground text-sm font-medium">
                {t('widget.data')}
              </p>
              <DataSourceForm dashboardId={dashboardId} source={source} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
